import { PropertyTypeEnum } from '~/core/entities/CRMEnums';

export { default as PoiAddressGridSection } from './Section.vue';

export interface LinkGroup {
	title: string;
	items: { label: string; href: string }[];
}

interface PoiAddress {
	displayName: string;
	href: string;
	formattedAddress: string;
	lat: number;
	lng: number;
}

interface PoiAddressGroupConfig {
	title: string;
	propertyType?: PropertyTypeEnum;
	addresses: PoiAddress[];
}

const PROPERTY_TYPE_LABEL_MAP: Partial<Record<PropertyTypeEnum, string>> = {
	[PropertyTypeEnum.APARTMENT]: 'Apartamentos',
	[PropertyTypeEnum.HOUSE]: 'Casas',
	[PropertyTypeEnum.CONDO_HOUSE]: 'Casas de condomínio'
};

function buildLabel(
	displayName: string,
	propertyType?: PropertyTypeEnum
): string {
	if (!propertyType) {
		return displayName;
	}
	const propertyTypeLabel = PROPERTY_TYPE_LABEL_MAP[propertyType] || '';
	return `${propertyTypeLabel} em ${displayName}`;
}

function createLinkGroup(config: PoiAddressGroupConfig): LinkGroup {
	return {
		title: config.title,
		items: config.addresses.map(address => {
			const label = buildLabel(address.displayName, config.propertyType);

			return {
				label,
				href: address.href
			};
		})
	};
}

// São Paulo configuration
const saoPauloGroupConfigs: PoiAddressGroupConfig[] = [
	{
		title: 'Imóveis próximos a ruas',
		addresses: [
			{
				displayName: 'Rua Haddock Lobo',
				href: '/venda/imoveis/jardim-america-sao-paulo-sp-brasil/rua-haddock-lobo',
				formattedAddress: 'Rua Haddock Lobo, Cerqueira César, São Paulo - SP',
				lat: -23.5616476,
				lng: -46.6656018
			},
			{
				displayName: 'Rua Bela Cintra',
				href: '/venda/imoveis/cerqueira-cesar-sao-paulo-sp-brasil/rua-bela-cintra',
				formattedAddress: 'Rua Bela Cintra, Consolação, São Paulo - SP',
				lat: -23.5532685,
				lng: -46.6576288
			},
			{
				displayName: 'Avenida Juriti',
				href: '/venda/imoveis/vila-uberabinha-sao-paulo-sp-brasil/avenida-juriti',
				formattedAddress: 'Avenida Juriti, Moema, São Paulo - SP',
				lat: -23.5999905,
				lng: -46.6642658
			},
			{
				displayName: 'Rua Joaquim Antunes',
				href: '/venda/imoveis/pinheiros-sao-paulo-sp-brasil/rua-joaquim-antunes',
				formattedAddress: 'Rua Joaquim Antunes, Pinheiros, São Paulo - SP',
				lat: -23.5641584,
				lng: -46.6817144
			},
			{
				displayName: 'Rua Tucumã',
				href: '/venda/imoveis/jardim-paulistano-sao-paulo-sp-brasil/rua-tucuma',
				formattedAddress: 'Rua Tucumã, Jardim Europa, São Paulo - SP',
				lat: -23.5817325,
				lng: -46.6895259
			}
		]
	},
	{
		title: 'Apartamentos próximos às ruas',
		propertyType: PropertyTypeEnum.APARTMENT,
		addresses: [
			{
				displayName: 'Rua Oscar Freire',
				href: '/venda/imoveis/jardim-america-sao-paulo-sp-brasil/rua-oscar-freire/apartamento',
				formattedAddress: 'Rua Oscar Freire, Jardim Paulista, São Paulo - SP',
				lat: -23.5686704,
				lng: -46.6626754
			},
			{
				displayName: 'Rua Haddock Lobo',
				href: '/venda/imoveis/jardim-america-sao-paulo-sp-brasil/rua-haddock-lobo/apartamento',
				formattedAddress: 'Rua Haddock Lobo, Cerqueira César, São Paulo - SP',
				lat: -23.5616476,
				lng: -46.6656018
			},
			{
				displayName: 'Rua Bela Cintra',
				href: '/venda/imoveis/consolacao-sao-paulo-sp-brasil/rua-bela-cintra/apartamento',
				formattedAddress: 'Rua Bela Cintra, Consolação, São Paulo - SP',
				lat: -23.5532685,
				lng: -46.6576288
			},
			{
				displayName: 'Rua Pernambuco',
				href: '/venda/imoveis/higienopolis-sao-paulo-sp-brasil/rua-pernambuco/apartamento',
				formattedAddress: 'Rua Pernambuco, Jardim Paulista, São Paulo - SP',
				lat: -23.5668185,
				lng: -46.6624944
			},
			{
				displayName: 'Rua Venezuela',
				href: '/venda/imoveis/jardim-paulista-sao-paulo-sp-brasil/rua-venezuela/apartamento',
				formattedAddress: 'Rua Venezuela, Jardim Paulista, São Paulo - SP',
				lat: -23.5655917,
				lng: -46.6713675
			}
		]
	},
	{
		title: 'Casas próximas às ruas',
		propertyType: PropertyTypeEnum.HOUSE,
		addresses: [
			{
				displayName: 'Rua Estados Unidos',
				href: '/venda/imoveis/jardim-america-sao-paulo-sp-brasil/rua-estados-unidos/casa',
				formattedAddress: 'Rua Estados Unidos, Jardim America, São Paulo - SP',
				lat: -23.5670894,
				lng: -46.6683854
			},
			{
				displayName: 'Rua Tabapuã',
				href: '/venda/imoveis/itaim-bibi-sao-paulo-sp-brasil/rua-tabapua/casa',
				formattedAddress: 'Rua Tabapuã, Itaim Bibi, São Paulo - SP',
				lat: -23.5839916,
				lng: -46.6789935
			},
			{
				displayName: 'Rua Pernambuco',
				href: '/venda/imoveis/higienopolis-sao-paulo-sp-brasil/rua-pernambuco/casa',
				formattedAddress: 'Rua Pernambuco, Higienópolis, São Paulo - SP',
				lat: -23.5428842,
				lng: -46.6613907
			},
			{
				displayName: 'Rua Curitiba',
				href: '/venda/imoveis/paraiso-sao-paulo-sp-brasil/rua-curitiba/casa',
				formattedAddress: 'Rua Curitiba, Paraíso, São Paulo - SP',
				lat: -23.5812737,
				lng: -46.6541037
			},
			{
				displayName: 'Rua Sergipe',
				href: '/venda/imoveis/higienopolis-sao-paulo-sp-brasil/rua-sergipe/casa',
				formattedAddress: 'Rua Sergipe, Higienópolis, São Paulo - SP',
				lat: -23.5474736,
				lng: -46.6589
			}
		]
	},
	{
		title: 'Imóveis próximos a pontos de interesse',
		addresses: [
			{
				displayName: 'Imóveis próximos à Praça Pereira Coutinho',
				href: '/venda/imoveis/vila-nova-conceicao-sao-paulo-sp-brasil/praca-pereira-coutinho',
				formattedAddress:
					'Praça Pereira Coutinho, Vila Nova Conceição, São Paulo - SP',
				lat: -23.593161,
				lng: -46.669434
			},
			{
				displayName: 'Imóveis próximos ao Parque do Ibirapuera',
				href: '/venda/imoveis/vila-nova-conceicao-sao-paulo-sp-brasil/parque-ibirapuera',
				formattedAddress: 'Parque Ibirapuera, Vila Mariana, São Paulo - SP',
				lat: -23.5874,
				lng: -46.6576
			},
			{
				displayName: 'Imóveis próximos ao Clube Athletico Paulistano',
				href: '/venda/imoveis/jardim-america-sao-paulo-sp-brasil/clube-athletico-paulistano',
				formattedAddress:
					'Club Athletico Paulistano, Jardim América, São Paulo - SP',
				lat: -23.568386,
				lng: -46.668963
			},
			{
				displayName: 'Imóveis próximos ao Parque do Povo',
				href: '/venda/imoveis/itaim-bibi-sao-paulo-sp-brasil/parque-do-povo',
				formattedAddress:
					'Parque do Povo Mário Pimenta Camargo, Avenida Henrique Chamma - Pinheiros, São Paulo - SP',
				lat: -23.587594,
				lng: -46.688417099999995
			},
			{
				displayName: 'Imóveis próximos ao Parque Villa-Lobos',
				href: '/venda/imoveis/alto-de-pinheiros-sao-paulo-sp-brasil/parque-villa-lobos',
				formattedAddress:
					'Parque Villa-Lobos, Avenida Professor Fonseca Rodrigues - Alto de Pinheiros, São Paulo - SP',
				lat: -23.5463088,
				lng: -46.725375799999995
			}
		]
	}
];

// Curitiba configuration
const curitibaGroupConfigs: PoiAddressGroupConfig[] = [
	{
		title: 'Imóveis próximos a ruas',
		addresses: [
			{
				displayName: 'Rua Brigadeiro Franco',
				href: '/venda/imoveis/agua-verde-curitiba-pr-brasil/rua-brigadeiro-franco',
				formattedAddress: 'Rua Brigadeiro Franco, Água Verde, Curitiba - PR',
				lat: -25.4534129,
				lng: -49.2709882
			},
			{
				displayName: 'Rua Prof. Pedro Viriato Parigot de Souza',
				href: '/venda/imoveis/mossungue-curitiba-pr-brasil/rua-professor-pedro-viriato-parigot-de-souza',
				formattedAddress:
					'Rua Professor Pedro Viriato Parigot de Souza, Mossunguê, Curitiba - PR',
				lat: -25.4362459,
				lng: -49.3252714
			},
			{
				displayName: 'Av. Visconde de Guarapuava',
				href: '/venda/imoveis/batel-curitiba-pr-brasil/avenida-visconde-de-guarapuava',
				formattedAddress: 'Av. Visc. de Guarapuava, Centro, Curitiba - PR',
				lat: -25.4361517,
				lng: -49.2672033
			},
			{
				displayName: 'Rua Dep. Heitor Alencar Furtado',
				href: '/venda/imoveis/campo-comprido-curitiba-pr-brasil/rua-deputado-heitor-alencar-furtado',
				formattedAddress:
					'Rua Deputado Heitor Alencar Furtado, Campina do Siqueira, Curitiba - PR',
				lat: -25.4378521,
				lng: -49.3133185
			},
			{
				displayName: 'Rua José Izidoro Biazetto',
				href: '/venda/imoveis/ecoville-curitiba-pr-brasil/rua-jose-izidoro-biazetto',
				formattedAddress: 'Rua José Izidoro Biazetto, Mossunguê, Curitiba - PR',
				lat: -25.4308742,
				lng: -49.3388725
			}
		]
	},
	{
		title: 'Apartamentos próximos às ruas',
		propertyType: PropertyTypeEnum.APARTMENT,
		addresses: [
			{
				displayName: 'Rua Prof. Pedro Viriato Parigot de Souza',
				href: '/venda/imoveis/mossungue-curitiba-pr-brasil/rua-professor-pedro-viriato-parigot-de-souza/apartamento',
				formattedAddress:
					'Rua Professor Pedro Viriato Parigot de Souza, Mossunguê, Curitiba - PR',
				lat: -25.4362459,
				lng: -49.3252714
			},
			{
				displayName: 'Rua Guilherme Pugsley',
				href: '/venda/imoveis/agua-verde-curitiba-pr-brasil/rua-guilherme-pugsley/apartamento',
				formattedAddress: 'Rua Guilherme Pugsley, Água Verde, Curitiba - PR',
				lat: -25.4589039,
				lng: -49.2871862
			},
			{
				displayName: 'Rua José Izidoro Biazetto',
				href: '/venda/imoveis/mossungue-curitiba-pr-brasil/rua-jose-izidoro-biazetto/apartamento',
				formattedAddress: 'Rua José Izidoro Biazetto, Mossunguê, Curitiba - PR',
				lat: -25.4308742,
				lng: -49.3388725
			},
			{
				displayName: 'Rua Martim Afonso',
				href: '/venda/imoveis/bigorrilho-curitiba-pr-brasil/rua-martim-afonso/apartamento',
				formattedAddress: 'Rua Martim Afonso, Bigorrilho, Curitiba - PR',
				lat: -25.4328735,
				lng: -49.2960704
			},
			{
				displayName: 'Rua Mariano Torres',
				href: '/venda/imoveis/centro-curitiba-pr-brasil/rua-mariano-torres/apartamento',
				formattedAddress: 'Rua Mariano Torres, Centro, Curitiba - PR',
				lat: -25.4314227,
				lng: -49.2621961
			}
		]
	},
	{
		title: 'Casas de condomínio próximas às ruas',
		propertyType: PropertyTypeEnum.CONDO_HOUSE,
		addresses: [
			{
				displayName: 'Rua Luiz Tramontin',
				href: '/venda/imoveis/campo-comprido-curitiba-pr-brasil/rua-luiz-tramontin/casa-de-condominio',
				formattedAddress: 'Rua Luiz Tramontin, Campo Comprido, Curitiba - PR',
				lat: -25.4555362,
				lng: -49.3337029
			},
			{
				displayName: 'Rua Luiz Zilli',
				href: '/venda/imoveis/campo-comprido-curitiba-pr-brasil/rua-luiz-zilli/casa-de-condominio',
				formattedAddress: 'Rua Luiz Zilli, Campo Comprido, Curitiba - PR',
				lat: -25.4554729,
				lng: -49.3252483
			},
			{
				displayName: 'Rua Hermenegildo Luca',
				href: '/venda/imoveis/sao-braz-curitiba-pr-brasil/rua-hermenegildo-luca/casa-de-condominio',
				formattedAddress: 'Rua Hermenegildo Luca, São Braz, Curitiba - PR',
				lat: -25.4103741,
				lng: -49.3611077
			},
			{
				displayName: 'Rua José Benedito Cottolengo',
				href: '/venda/imoveis/campo-comprido-curitiba-pr-brasil/rua-jose-benedito-cottolengo/casa-de-condominio',
				formattedAddress:
					'Rua José Benedito Cottolengo, Campo Comprido, Curitiba - PR',
				lat: -25.4645328,
				lng: -49.3259817
			},
			{
				displayName: 'Rua Tobias de Macedo Júnior',
				href: '/venda/imoveis/santo-inacio-curitiba-pr-brasil/rua-tobias-de-macedo-junior/casa-de-condominio',
				formattedAddress:
					'Rua Tobias de Macedo Júnior, Santo Inácio, Curitiba - PR',
				lat: -25.4272583,
				lng: -49.3292898
			}
		]
	},
	{
		title: 'Imóveis próximos a pontos de interesse',
		addresses: [
			{
				displayName: 'Imóveis próximos ao Jardim Botânico',
				href: '/venda/imoveis/jardim-botanico-curitiba-pr-brasil',
				formattedAddress:
					'Jardim Botânico de Curitiba, Rua Engenheiro Ostoja Roguski - Jardim Botânico, Curitiba - PR',
				lat: -25.443121299999998,
				lng: -49.2382445
			},
			{
				displayName: 'Imóveis próximos ao Parque Barigui',
				href: '/venda/imoveis/bigorrilho-curitiba-pr-brasil/parque-barigui',
				formattedAddress:
					'Parque Barigui, Avenida Cândido Hartmann - Bigorrilho, Curitiba - PR',
				lat: -25.4262654,
				lng: -49.30723
			},
			{
				displayName: 'Imóveis próximos ao Parque Tanguá',
				href: '/venda/imoveis/taboao-curitiba-pr-brasil/parque-tangua',
				formattedAddress:
					'Parque Tanguá, Rua Oswaldo Maciel - Taboão, Curitiba - PR',
				lat: -25.3790661,
				lng: -49.282423300000005
			},
			{
				displayName: 'Imóveis próximos ao Parque Tingui',
				href: '/venda/imoveis/sao-joao-curitiba-pr-brasil/parque-tingui',
				formattedAddress:
					'Parque Tingui, Avenida Fredolin Wolf - Pilarzinho, Curitiba - PR',
				lat: -25.4024187,
				lng: -49.305336399999995
			},
			{
				displayName: 'Imóveis próximos ao Pátio Batel',
				href: '/venda/imoveis/batel-curitiba-pr-brasil/patio-batel',
				formattedAddress:
					'Shopping Pátio Batel, Avenida do Batel - Batel, Curitiba - PR',
				lat: -25.442929499999998,
				lng: -49.290813299999996
			}
		]
	}
];

// Generate link groups
export const saoPauloGroups: LinkGroup[] = saoPauloGroupConfigs.map(config =>
	createLinkGroup(config)
);

export const curitibaGroups: LinkGroup[] = curitibaGroupConfigs.map(config =>
	createLinkGroup(config)
);
